import { ReactNode, useEffect, useId, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { XIcon } from 'lucide-react'

import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

type WishlistDialogProps = {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer: ReactNode
}

const WishlistDialog = ({ open, title, onClose, children, footer }: WishlistDialogProps) => {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const { strings } = useAppStore()

  useEffect(() => {
    if (!open) return

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    closeButtonRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )

      if (!focusableElements?.length) {
        event.preventDefault()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [open])

  return (
    <Transition in={open} nodeRef={dialogRef} timeout={500} unmountOnExit>
      {(state) => (
        <div
          ref={dialogRef}
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose()
          }}
          className={cn(
            'fixed inset-0 z-dialog bg-dark/20 backdrop-blur-sm',
            'flex items-center justify-center',
            '!m-0 p-[1rem] md:p-[2rem]',
            'transition-opacity duration-500 ease-out-cubic',
            {
              'opacity-100': state === 'entering' || state === 'entered',
              'opacity-0': state === 'exiting' || state === 'exited'
            }
          )}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            className={cn(
              'relative h-auto w-full bg-white text-dark',
              'rounded-[1rem] border border-dark/30 p-[2rem] shadow-sm',
              'grid gap-y-[2rem]',
              'max-w-[52rem]'
            )}
          >
            <div className="flex items-center justify-between">
              <Typography
                id={titleId}
                variant="small"
                component="h2"
                className="font-bold uppercase"
              >
                {title}
              </Typography>
              <button
                type="button"
                ref={closeButtonRef}
                onClick={onClose}
                className={cn(
                  'flex shrink-0',
                  'm-[-1.2rem] rounded-[0.5rem] p-[1.2rem]',
                  'transition-colors duration-300 ease-out-cubic hover:bg-light',
                  'focus-ring-dark'
                )}
                aria-label={strings?.labels?.close}
              >
                <XIcon size={18} />
              </button>
            </div>

            <div id={descriptionId} className="max-w-[90%]">
              {children}
            </div>

            <div
              className={cn(
                'flex items-center justify-end',
                'mx-[-2rem] mb-[-2rem] p-[2rem]',
                'border-t bg-dark/5'
              )}
            >
              {footer}
            </div>
          </div>
        </div>
      )}
    </Transition>
  )
}

export default WishlistDialog
